import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOrgData } from "@/hooks/useOrgData";
import WorkerSheet from "@/components/WorkerSheet";
import ScannerQrButton from "@/components/ScannerQrButton";
import { Users, AlertTriangle, Clock, Search, FolderOpen } from "lucide-react";
import { RiskBadge } from "@/components/RiskBadge";
import { cleanRut } from "@/lib/rut";

function Kpi({ icon: Icon, title, value, sub, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-3xl font-display font-bold text-slate-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${accent}`}><Icon size={20} /></div>
    </div>
  );
}

function Ring({ value }) {
  const r = 20, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <svg width="52" height="52" className="rotate-[-90deg]">
      <circle cx="26" cy="26" r={r} stroke="#E2E8F0" strokeWidth="6" fill="none" />
      <circle cx="26" cy="26" r={r} stroke="#2563EB" strokeWidth="6" fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
    </svg>
  );
}

const STATUS_LABEL = {
  ACTIVE: { label: "Activo", cls: "bg-blue-50 text-blue-700" },
  FINIQUITADO: { label: "Finiquitado", cls: "bg-slate-100 text-slate-500" },
  TRASLADO: { label: "En traslado", cls: "bg-amber-50 text-amber-700" },
  COMPLETED: { label: "Completado", cls: "bg-emerald-50 text-emerald-700" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const { workers, reload } = useOrgData();
  const [stats, setStats] = useState(null);
  const [openWorker, setOpenWorker] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const qRut = cleanRut(q); // dígitos+K sin puntos ni guión
    return workers.filter((w) => {
      const full = `${w.first_name || ""} ${w.last_name || ""}`.toLowerCase();
      if (full.includes(q)) return true;
      const rutNorm = cleanRut(w.rut || "");
      return qRut && rutNorm.includes(qRut);
    }).slice(0, 20);
  }, [query, workers]);

  const refreshAll = () => { reload(); api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between animate-fadein">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">¡Hola, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-slate-500 mt-1">Busca cualquier trabajador por nombre o RUT y abre su expediente.</p>
        </div>
        <div className="flex items-center gap-3">
          <ScannerQrButton />
          <p className="text-sm text-slate-400 capitalize">{new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
      </div>

      {/* Buscador de expediente — compacto, arriba */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-slate-600 shrink-0">
            <FolderOpen size={16} className="text-blue-600" />
            <span className="text-sm font-medium hidden sm:block">Expediente</span>
          </div>
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="dashboard-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre o RUT (cualquier formato)…"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-blue-400 focus:bg-white outline-none text-sm transition-colors"
            />
          </div>
        </div>

        {query && (
          <div className="mt-3">
            {results.length === 0 ? (
              <p data-testid="dashboard-no-results" className="text-sm text-slate-400 py-4 text-center">
                Sin coincidencias para "<span className="font-medium">{query}</span>".
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-slate-500 bg-slate-50 border-b border-slate-100">
                      <th className="text-left py-2 px-3">Trabajador</th>
                      <th className="text-left py-2 px-3">RUT</th>
                      <th className="text-left py-2 px-3">Mandante / Contrato</th>
                      <th className="text-left py-2 px-3">Estado</th>
                      <th className="text-left py-2 px-3">Riesgo</th>
                      <th className="text-right py-2 px-3">Avance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((w) => {
                      const st = STATUS_LABEL[w.status] || STATUS_LABEL.ACTIVE;
                      return (
                        <tr key={w.id} data-testid={`dashboard-row-${w.id}`}
                          onClick={() => setOpenWorker(w.id)}
                          className="border-b border-slate-50 last:border-0 cursor-pointer hover:bg-blue-50/40 transition-colors">
                          <td className="py-2.5 px-3">
                            <p className="font-semibold text-slate-800">{w.first_name} {w.last_name}</p>
                            <p className="text-xs text-slate-400">{w.position || "—"}</p>
                          </td>
                          <td className="py-2.5 px-3 font-mono text-slate-600">{w.rut || "—"}</td>
                          <td className="py-2.5 px-3">
                            <p className="text-slate-700">{w.mandante || w.company || <span className="text-slate-400">Sin asignación</span>}</p>
                            {w.contrato && <p className="text-xs text-slate-400">{w.contrato}</p>}
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                          </td>
                          <td className="py-2.5 px-3"><RiskBadge level={w.risk_level} /></td>
                          <td className="py-2.5 px-3 text-right">
                            <div className="inline-flex items-center gap-2">
                              <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500" style={{ width: `${w.progress_percentage || 0}%` }} />
                              </div>
                              <span className="font-semibold text-slate-700 text-xs w-9 text-right">{w.progress_percentage || 0}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi icon={Users} title="Trabajadores en proceso" value={stats?.active ?? "—"} sub={`${stats?.completed ?? 0} completados`} accent="bg-blue-50 text-blue-600" />
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center justify-between">
          <div><p className="text-sm text-slate-500">Avance promedio</p><p className="text-3xl font-display font-bold text-slate-900 mt-1">{stats?.avg_progress ?? 0}%</p></div>
          <div className="relative flex items-center justify-center"><Ring value={stats?.avg_progress ?? 0} /></div>
        </div>
        <Kpi icon={AlertTriangle} title="Con riesgo de atraso" value={(stats?.by_risk?.HIGH ?? 0) + (stats?.by_risk?.CRITICAL ?? 0)} sub={`${stats?.by_risk?.HIGH ?? 0} Alto · ${stats?.by_risk?.CRITICAL ?? 0} Crítico`} accent="bg-red-50 text-red-600" />
        <Kpi icon={Clock} title="Pendientes de contacto" value={stats?.pending_contact ?? "—"} sub="Sin actividad > 48h" accent="bg-amber-50 text-amber-600" />
      </div>

      <WorkerSheet workerId={openWorker} open={!!openWorker} onClose={() => setOpenWorker(null)}
        canEdit={user?.role !== "VIEWER"} onChanged={refreshAll} />
    </div>
  );
}
